import React, { useState, useEffect, useRef, useCallback } from 'react';
import useKioskManifest from '../hooks/useKioskManifest';

const API = process.env.REACT_APP_URL;
const KIOSK_AUTH_KEY = 'kiosk_task_auth';

/* ── Geo helpers ── */
function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('GPS not supported'));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      err => reject(err.code === 1
        ? new Error('Location access denied. Tap the lock icon in your address bar, set Location to "Allow", then reload.')
        : new Error('Unable to get location. Enable GPS and try again.')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

function getSavedAuth() {
  try {
    const raw = localStorage.getItem(KIOSK_AUTH_KEY);
    if (!raw) return null;
    const auth = JSON.parse(raw);
    if (auth.expiresAt && Date.now() < auth.expiresAt) return auth;
    localStorage.removeItem(KIOSK_AUTH_KEY);
    return null;
  } catch { return null; }
}

/* ── Dynamic favicon ── */
function setFavicon() {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d');
  // Background
  const g = ctx.createLinearGradient(0, 0, 64, 64);
  g.addColorStop(0, '#f59e0b'); g.addColorStop(1, '#d97706');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(32, 32, 32, 0, Math.PI * 2); ctx.fill();
  // Checkmark icon
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  ctx.beginPath(); ctx.moveTo(16, 34); ctx.lineTo(28, 46); ctx.lineTo(48, 20); ctx.stroke();
  // MYC text
  ctx.fillStyle = '#fff'; ctx.font = 'bold 11px Arial'; ctx.textAlign = 'center';
  ctx.fillText('MYC', 32, 60);
  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.rel = 'icon'; link.href = canvas.toDataURL();
  document.head.appendChild(link);
  document.title = 'MYC — Quick Task';
}

export default function KioskTask() {
  useKioskManifest('/manifest-task.json', '#4c1d95');
  const [kioskAuth, setKioskAuth] = useState(() => getSavedAuth());
  const [authUser, setAuthUser] = useState('');
  const [authOtp, setAuthOtp] = useState('');
  const [authStep, setAuthStep] = useState('username');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authResend, setAuthResend] = useState(0);
  const [authPassword, setAuthPassword] = useState('');
  const authTimerRef = useRef(null);
  const otpRef = useRef(null);
  const pwRef = useRef(null);

  const [geoState, setGeoState] = useState('checking');
  const [geoError, setGeoError] = useState('');
  const [coords, setCoords] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const deferredPrompt = useRef(null);

  const [staffList, setStaffList] = useState([]);
  const emptyForm = { title: '', assignee: '', notes: '', urgentLevel: 'Scheduled', estimatedEndDate: '' };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  // Set favicon on mount
  useEffect(() => { setFavicon(); }, []);

  // ── Install prompt (Android) + iOS instructions ──
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; setShowInstall(true); };
    window.addEventListener('beforeinstallprompt', handler);
    if (!localStorage.getItem('kiosk_task_install_prompted')) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS || !('beforeinstallprompt' in window)) {
        setShowInstall(true);
        localStorage.setItem('kiosk_task_install_prompted', '1');
      }
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
    }
    setShowInstall(false);
    localStorage.setItem('kiosk_task_install_prompted', '1');
  };

  // Geo check
  useEffect(() => {
    (async () => {
      try { const c = await getGeo(); setCoords(c); setGeoState('ok'); }
      catch (err) { setGeoState(err.message.includes('denied') ? 'denied' : 'error'); setGeoError(err.message); }
    })();
  }, []);

  // Fetch staff list once authenticated
  useEffect(() => {
    if (!kioskAuth?.token) return;
    fetch(`${API}/users`, { headers: { 'Authorization': `Bearer ${kioskAuth.token}`, 'x-username': kioskAuth.username, 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(data => {
        setStaffList(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, [kioskAuth]);

  // Auth handlers
  function startAuthResend() {
    setAuthResend(60);
    clearInterval(authTimerRef.current);
    authTimerRef.current = setInterval(() => {
      setAuthResend(t => { if (t <= 1) { clearInterval(authTimerRef.current); return 0; } return t - 1; });
    }, 1000);
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!authUser.trim()) return setAuthError('Please enter your username.');
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API}/kiosk-otp/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: authUser.trim(), userLat: coords?.lat, userLon: coords?.lon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      if (data.mode === 'password') { setAuthStep('password'); }
      else { setAuthStep('otp'); startAuthResend(); if (data.devOtp) setAuthError(`[DEV] OTP: ${data.devOtp}`); }
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const isPassword = authStep === 'password';
    if (!isPassword && (!authOtp || authOtp.length !== 4)) return setAuthError('Enter the 4-digit OTP.');
    if (isPassword && !authPassword) return setAuthError('Enter your password.');
    setAuthLoading(true); setAuthError('');
    try {
      const body = { username: authUser.trim() };
      if (isPassword) body.password = authPassword; else body.otp = authOtp.trim();
      const res = await fetch(`${API}/kiosk-otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || (isPassword ? 'Invalid password' : 'Invalid OTP'));
      // Check permissions: needs remoteTaskAccess or task-related permission
      const roles = data.data.roles || [];
      const perms = data.data.permissions || [];
      const isSuper = roles.some(r => ['owner', 'solution'].includes(r));
      if (!isSuper && !data.data.remoteTaskAccess && !perms.includes('checklist.add')) {
        throw new Error('You do not have permission to create tasks remotely.');
      }
      const kioskDays = data.data.kioskSessionDays || 5;
      const authObj = { token: data.data.token, username: data.data.username, expiresAt: Date.now() + kioskDays * 24 * 60 * 60 * 1000 };
      localStorage.setItem(KIOSK_AUTH_KEY, JSON.stringify(authObj));
      setKioskAuth(authObj);
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  useEffect(() => {
    if (authStep === 'otp' && otpRef.current) otpRef.current.focus();
    if (authStep === 'password' && pwRef.current) pwRef.current.focus();
  }, [authStep]);

  const set = useCallback((field) => (e) => { setForm(f => ({ ...f, [field]: e.target.value })); setError(''); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Please enter a task title.');
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${kioskAuth.token}`, 'x-username': kioskAuth.username },
        body: JSON.stringify({
          task: {
            title: form.title.trim(),
            assignee: form.assignee,
            createdBy: kioskAuth.username,
            frequency: 'OneTime',
            type: 'OneTime',
            urgentLevel: form.urgentLevel,
            notes: form.notes,
            estimatedEndDate: form.estimatedEndDate || undefined,
            status: 'new',
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data === 'string' ? data : data.message || 'Failed to create task');
      setSuccess({ title: form.title, assignee: form.assignee });
      setTimeout(() => { setSuccess(null); setForm(emptyForm); }, 6000);
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  // Styles
  const pageStyle = { minHeight: '100vh', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 16px 40px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
  const cardStyle = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,.08)', width: '100%', maxWidth: 480, overflow: 'hidden' };
  const headerStyle = { background: 'linear-gradient(135deg, #d97706, #f59e0b)', padding: '24px 24px 20px', color: '#fff', textAlign: 'center' };
  const inputStyle = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b' };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4, display: 'block' };
  const authInputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 16, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b', textAlign: 'center', letterSpacing: 1 };
  const btnStyle = (loading) => ({
    width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #d97706, #f59e0b)',
    color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
  });

  // Auth gate
  if (geoState === 'ok' && !kioskAuth) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, maxWidth: 420 }}>
          <div style={headerStyle}>
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
            <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Staff Authentication</div>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            {authStep === 'username' && (
              <form onSubmit={handleRequestOtp}>
                <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>Enter your staff username to continue.</div>
                <input type="text" value={authUser} onChange={e => { setAuthUser(e.target.value); setAuthError(''); }} placeholder="Username" style={authInputStyle} autoComplete="off" autoFocus />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#d97706' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={btnStyle(authLoading)}>{authLoading ? 'Checking...' : 'Continue'}</button>
              </form>
            )}
            {authStep === 'otp' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthOtp(''); setAuthError(''); }} style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>OTP sent to your registered WhatsApp number.</div>
                <input ref={otpRef} type="number" value={authOtp} onChange={e => { setAuthOtp(e.target.value.slice(0, 4)); setAuthError(''); }} placeholder="4-digit OTP" style={authInputStyle} inputMode="numeric" />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#d97706' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, textAlign: 'center' }}>
                  {authResend > 0 ? `Resend in ${authResend}s` : <button type="button" onClick={handleRequestOtp} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d97706', fontSize: 12, textDecoration: 'underline' }}>Resend OTP</button>}
                </div>
                <button type="submit" disabled={authLoading} style={btnStyle(authLoading)}>{authLoading ? 'Verifying...' : 'Verify & Continue'}</button>
              </form>
            )}
            {authStep === 'password' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthPassword(''); setAuthError(''); }} style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>Enter your password to continue.</div>
                <input ref={pwRef} type="password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }} placeholder="Password" style={{ ...authInputStyle, letterSpacing: 3 }} />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={btnStyle(authLoading)}>{authLoading ? 'Verifying...' : 'Login & Continue'}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Geo checking
  if (geoState === 'checking') {
    return (<div style={{ ...pageStyle, justifyContent: 'center' }}><div style={{ textAlign: 'center', color: '#64748b' }}><div style={{ fontSize: 36, marginBottom: 12 }}>📍</div><div style={{ fontSize: 16, fontWeight: 600 }}>Checking location...</div><div style={{ fontSize: 13, marginTop: 4 }}>Please allow location access when prompted.</div></div></div>);
  }
  if (geoState === 'denied' || geoState === 'error') {
    return (<div style={{ ...pageStyle, justifyContent: 'center' }}><div style={{ ...cardStyle, maxWidth: 400, padding: 32, textAlign: 'center' }}><div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div><h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Location Required</h2><p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{geoError || 'Please enable GPS.'}</p><button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#d97706', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Retry</button></div></div>);
  }

  // Success
  if (success) {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ ...cardStyle, maxWidth: 420, textAlign: 'center', overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)', padding: '32px 24px 24px', color: '#fff' }}>
            <div style={{ fontSize: 56, marginBottom: 8, lineHeight: 1 }}>✅</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 6px' }}>Task Created!</h2>
            <p style={{ fontSize: 14, opacity: 0.9, margin: 0 }}>{success.title}</p>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            <div style={{ background: '#fffbeb', border: '2px solid #fde68a', borderRadius: 14, padding: '16px 20px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#d97706', fontWeight: 700, textTransform: 'uppercase' }}>Assigned To</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#92400e', marginTop: 4 }}>{success.assignee}</div>
            </div>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>This screen will reset automatically...</p>
            <button onClick={() => { setSuccess(null); setForm(emptyForm); }} style={{ padding: '10px 24px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Create Another Task</button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={pageStyle}>
      {showInstall && (
        <div style={{ width: '100%', maxWidth: 480, background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #4c1d95, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(76,29,149,0.3)' }}>
            <span style={{ color: '#fff', fontSize: 18 }}>{'\u2705'}</span>
          </div>
          <div style={{ flex: 1, fontSize: 13, color: '#4c1d95', lineHeight: 1.5 }}>
            <strong>Install App:</strong> Tap <strong>Share → Add to Home Screen</strong> to install as a standalone app.
          </div>
          <button onClick={handleInstall} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#4c1d95', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {deferredPrompt.current ? 'Install' : 'Got it'}
          </button>
        </div>
      )}
      <div style={cardStyle}>
        <div style={{ ...headerStyle, position: 'relative' }}>
          <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Medical Yoga Therapy Centre</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '14px 0 0', opacity: 0.95 }}>Quick Task</h1>
          {kioskAuth && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>Staff: {kioskAuth.username}</div>}
          {kioskAuth && (
            <button onClick={() => { localStorage.removeItem(KIOSK_AUTH_KEY); setKioskAuth(null); setAuthStep('username'); setAuthUser(''); }}
              style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: '#fff', fontWeight: 600 }}>Logout</button>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 28px' }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Task Title *</label>
              <input type="text" value={form.title} onChange={set('title')} style={inputStyle} placeholder="What needs to be done?" autoComplete="off" autoFocus />
            </div>

            <div>
              <label style={labelStyle}>Assign To</label>
              <select value={form.assignee} onChange={set('assignee')} style={inputStyle}>
                <option value="">Unassigned (optional)</option>
                {staffList.map(u => <option key={u.username} value={u.username}>{u.username}{u.designation ? ` (${u.designation})` : ''}</option>)}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Priority</label>
                <select value={form.urgentLevel} onChange={set('urgentLevel')} style={inputStyle}>
                  <option value="Urgent">Urgent</option>
                  <option value="Important">Important</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Some Day">Some Day</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Due Date</label>
                <input type="date" value={form.estimatedEndDate} onChange={set('estimatedEndDate')} style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes} onChange={set('notes')} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Additional details..." />
            </div>
          </div>

          {error && <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>{error}</div>}

          <button type="submit" disabled={submitting} style={{ ...btnStyle(submitting), marginTop: 20, boxShadow: '0 2px 8px rgba(217,119,6,.25)' }}>
            {submitting ? 'Creating...' : 'Create Task'}
          </button>
        </form>
      </div>

      <div style={{ marginTop: 20, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>MYC Quick Task Kiosk</div>
    </div>
  );
}
